import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import DeleteIcon from '../../svgs/DeleteIcon';
import MoreVertIcon from '../../svgs/MoreVertIcon';
import TimelinePhotoIcon from '../../svgs/TimelinePhotoIcon';
import { useOutsideAlerter } from '../transcript/useClickOutside';

import type { PunchListItem } from '../../types.ts/story';
import type { SetStateAction } from 'react';

type Props = {
  punchListItem: PunchListItem;
  selectedPunchListId: string | undefined;
  setSelectedPunchListId: (value: SetStateAction<string | undefined>) => void;
  toggleModal: (
    value: SetStateAction<{
      item: PunchListItem | null;
      archive: PunchListItem | null;
    }>,
  ) => void;
};
const PunchListContentItem = observer((props: Props) => {
  const {
    punchListItem,
    toggleModal,
    selectedPunchListId,
    setSelectedPunchListId,
  } = props;
  const videoCreator = useVideoCreatorStore();
  const [isActionClicked, setIsActionClicked] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useOutsideAlerter(buttonRef, () => {
    setIsActionClicked(false);
  });

  if (!punchListItem.line) return null;

  const line = punchListItem.line;
  const description = punchListItem.description;
  const isDisabled = videoCreator.addedPunchListItemId === punchListItem.id;

  return (
    <Entry
      key={punchListItem.id}
      onClick={() => {
        if (isDisabled) return;
        toggleModal({ item: punchListItem, archive: punchListItem });
        setSelectedPunchListId(punchListItem.id);
      }}
      isDisabled={isDisabled}
      isSelected={selectedPunchListId === punchListItem.id}
    >
      <ImageWrapper
        hasImage={!!punchListItem.artifactSrc}
        onClick={() => {
          runInAction(async () => {
            videoCreator.setActiveElements(punchListItem.id!.toString());
          });
        }}
      >
        {punchListItem.artifactSrc ? (
          <Image src={punchListItem.artifactSrc} />
        ) : (
          <TimelinePhotoIcon strokeColor="var(--text-muted)" />
        )}
      </ImageWrapper>

      <Text>
        <div className="quote">{line}</div>
        <TextDescription>
          <div className="text">{description}</div>
          <div
            className="ellipsis"
            onClick={e => {
              e.stopPropagation();
              setIsActionClicked(true);
            }}
          >
            <MoreVertIcon width="14" height="14" />
          </div>
        </TextDescription>
      </Text>
      <RemoveButton
        isVisible={isActionClicked}
        ref={buttonRef}
        disabled={isDisabled}
        onClick={async e => {
          e.stopPropagation();
          if (punchListItem.id) {
            videoCreator.punchListManager.removePunchListItem(punchListItem.id);
            await videoCreator.removeAPhotoHighlightTranscriptChange(
              punchListItem.id,
            );
          }
          setIsActionClicked(false);
        }}
      >
        Remove <DeleteIcon />
      </RemoveButton>
    </Entry>
  );
});

export default PunchListContentItem;

const Entry = styled.div<{ isSelected: boolean; isDisabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  margin: 0 auto;
  cursor: pointer;
  border-radius: var(--radius);
  height: 59px;
  position: relative;

  ${props =>
    props.isSelected &&
    css`
      background-color: var(--border);
      border-radius: var(--radius);
    `}
`;

const Text = styled.div`
  width: 65%;
  padding-right: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  height: 59px;

  .quote {
    width: 100%;
    white-space: nowrap;
    color: var(--foreground);
    font-weight: var(--font-weight-normal);
    line-height: 19.2px;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: var(--text-xs);
  }
`;

const TextDescription = styled.div`
  display: flex;
  gap: var(--spacing-2xs);
  .text {
    color: var(--text-secondary);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-normal);
    /* line-height was 12px against a 12px font — a ratio of 1.0, so the lines
       almost touched and three of them read as one dense block. --leading-text-xs
       is 16px. Two lines at 16px is 32px, which fits the 38px box; three would
       need 48px and would have clipped mid-line. */
    line-height: var(--leading-text-xs);
    width: 90%;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 32px;
  }
  .ellipsis {
    width: 10%;
    margin-left: auto;
    text-align: right;
  }
`;

const ImageWrapper = styled.div<{ hasImage: boolean }>`
  background:
    linear-gradient(0deg, var(--border) 0%, var(--border) 100%),
    lightgray 50% / cover no-repeat;
  border: 1px solid var(--border-subtle);
  color: var(--border);
  width: 90px;
  height: 58px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 5px;
  object-position: top;
`;

const RemoveButton = styled.button<{ isVisible: boolean }>`
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  position: absolute;
  right: 0;
  bottom: -28px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--background);
  box-shadow: var(--elevation-toast);
  padding: 15px var(--spacing-sm);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  gap: 25px;
  z-index: 1;
  cursor: pointer;
`;
